import { moveInOrOut } from '../action';
import { drawAlignmentHelper, getOrCreateSnapEngine } from '../lib/align';
import {
  clipEventActions,
  getInteractiveImageClipHandlers,
  renderClipOverlay,
} from '../lib/image-clip';
import { drawRectRegion, renderInteractiveNodeBorder } from '../lib/interaction/label';
import {
  addPointAtSegment,
  getInteractiveLineHandlers,
  hitTestLineNode,
  renderInteractiveLineBorder,
} from '../lib/interaction/line';
import { moveAction, regionMoveAction } from '../lib/interaction/move';
import { getCurrentSelectableNodes } from '../lib/interaction/node';
import {
  addPathPointAtSegment,
  getInteractivePathHandlers,
  pathEditAction,
} from '../lib/interaction/path';
import {
  getInteractiveRadiusHandlers,
  isRadiusableNode,
  radiusAction,
  renderRadiusHandler,
} from '../lib/interaction/radius';
import {
  renderEdgeHandler,
  renderInteractiveGroupBorder,
  renderLineMidPointHandler,
  renderLinePointHandler,
  renderPathAnchorHandler,
  renderPathControlHandler,
  renderPointHandler,
  renderRotateHandler,
} from '../lib/interaction/render';
import {
  drawScrollbars,
  eventToScreenPoint,
  getScrollbarHandlers,
  scrollbarMoveAction,
} from '../lib/interaction/scrollbar';
import {
  type SectionMoveHandler,
  getSectionMoveHandlers,
  renderSectionMoveHandler,
  sectionMoveAction,
} from '../lib/interaction/section-move';
import {
  drawSmartLayoutButtons,
  getSmartLayoutHandlers,
  smartLayoutAction,
} from '../lib/interaction/smart-layout';
import { drawMeasureGuides } from '../lib/interaction/measure';
import { styleBrushAction } from '../lib/interaction/style-brush';
import {
  captureMoveDistanceAction,
  captureStateAction,
  doubleClickAction,
  editTextAction,
  hoverAction,
  hoverHandlerAction,
  insertAction,
  moveModeAction,
  regionResizeAction,
  regionRotateAction,
  resizeAction,
  selectAction,
  selectByRegionAction,
  textSelectionAction,
  unselectAction,
} from '../lib/interactive';
import { Rect, Region } from '../lib/region';
import {
  drawSpacingHandles,
  getSpacingHandlers,
  spacingAdjustAction,
  spacingEqualizeAction,
} from '../lib/spacing';
import { EventTypes, LayoutSizeType, TYPES } from '../render/constants';
import type { RenderContext } from '../render/context/createRenderContext';
import type { CursorProvider } from './cursor';
import type { SelectionProvider } from './selection';
import type { NodeType } from '../render/context/node';
import { toType } from '../common-utils';

// Type Definitions

/** Basic point with x, y coordinates */
export interface Point {
  x: number;
  y: number;
}

/** Screen point with additional metadata */
export interface ScreenPoint extends Point {
  fromStage?: boolean;
}

/** Mouse/Touch event-like interface */
export interface InteractiveEvent {
  clientX: number;
  clientY: number;
  target: EventTarget | null;
  button?: number;
  preventDefault?: () => void;
}

export interface SelectionRegion extends Region {
  type?: 'selection';
}

/** Base handler interface */
export interface BaseHandler {
  id: string;
  type: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  contains: (x: number, y: number) => boolean;
  node?: NodeType;
  [key: string]: any;
}

/** Point handler for resize corners */
export interface PointHandler extends BaseHandler {
  type: 'point';
  id: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

/** Edge handler for resize edges */
export interface EdgeHandler extends BaseHandler {
  type: 'edge';
  id: 'top' | 'right' | 'bottom' | 'left';
  angle?: number;
  vertical?: boolean;
  style?: string;
}

/** Rotation handler */
export interface RotateHandler extends BaseHandler {
  type: 'rotate';
  id: 'rotate';
  anchor?: Point;
  visualRadius?: number;
  rotate?: RotationState;
}

/** Line point handler */
export interface LinePointHandler extends BaseHandler {
  type: 'linePoint';
  pointIndex: number;
}

/** Line midpoint handler */
export interface LineMidPointHandler extends BaseHandler {
  type: 'lineMidPoint';
  segmentIndex: number;
}

/** Path point handler */
export interface PathPointHandler extends BaseHandler {
  type: 'pathPoint';
  pointIndex: number;
}

/** Path control handler */
export interface PathControlHandler extends BaseHandler {
  type: 'pathControl';
  pointIndex: number;
  controlType: 'in' | 'out';
}

/** Path midpoint handler */
export interface PathMidPointHandler extends BaseHandler {
  type: 'pathMidPoint';
  segmentIndex: number;
}

/** Union type for all handlers */
export type Handler =
  | SectionMoveHandler
  | PointHandler
  | EdgeHandler
  | RotateHandler
  | LinePointHandler
  | LineMidPointHandler
  | PathPointHandler
  | PathControlHandler
  | PathMidPointHandler
  | BaseHandler;

/** Node being moved with cached initial state */
export interface MovingNode {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  node: NodeType;
}

/** Clip mode state for image editing */
export interface ClipMode {
  nodeId: string;
  state: {
    startPoint: Point | null;
    cache: any;
    resizeEdge: string | null;
  };
}

/** Path mode state for path editing */
export interface PathMode {
  nodeId: string;
  state: {
    startPoint: Point | null;
    cache: any;
    resizeEdge: string | null;
  };
}

/** Rotation state during rotation transformation */
export interface RotationState {
  center: Point;
  startAngle: number;
  baseRotation: number;
}

/** Spacing adjustment state */
export interface SpacingState {
  [key: string]: any;
}

/** Main interactive provider interface */
export interface Interactive {
  cursor: CursorProvider;
  render: (renderContext: RenderContext) => void;
  stageContext: RenderContext;
  requestFocus: () => void;
  dispose: () => void;
  selectMode: string;
  createHitTestContext: typeof createHitTestContext;
  screenX: number;
  screenY: number;
  x: number;
  y: number;
  isTouchEditingOrDragging: boolean;
  getMovingNodeInitialBox: (id: string) => NodeType;
  clipMode: ClipMode | null;
  enterClipMode: (node: NodeType | null) => void;
  exitClipMode: () => void;
  pathMode: PathMode | null;
  enterPathMode: (node: NodeType | null) => void;
  exitPathMode: () => void;
  exitEditMode: () => void;
  eventToPoint: (e: InteractiveEvent) => Point;
  cacheMovingNode?: Record<string, NodeType>;
  movingNodes?: MovingNode[];
  previousSelectedNodes?: NodeType[];
  previousPointDownStart?: Point | null;
  pointStart?: Point | null;
  maxMoveDistance?: number | null;
  rotationState?: RotationState | null;
  isRealMove?: boolean | null;
  areaStart?: Point | null;
  areaEnd?: Point | null;
  movingHandler?: Handler | null;
  alignmentResult?: any;
  createNode?: any;
  altCreated?: boolean;
  skipSelectFromUp?: boolean;
  insertedNode?: NodeType | null;
  moveOutCache?: any;
  alignCache?: any;
  clipDrag?: any;
  brush?: any;
  hoverHandlerId?: string;
  spacingState?: SpacingState | null;
  createNodeCallback?: (() => void) | null;
  [key: string]: any;
}

/** Hit test context result */
export interface HitTestContext {
  type: string;
  point: Point;
  screenPoint: ScreenPoint;
  deltaX: number;
  deltaY: number;
  screenDeltaX: number;
  screenDeltaY: number;
  targetNode: NodeType | null;
  shallowNode: NodeType | null;
  depthNode: NodeType | null;
  targetHandler: Handler | null;
  targetSelectedNode: NodeType | null;
  targetRegion: (Rect & { nodes?: NodeType[] }) | null;
  selectedNodes: NodeType[];
  currentSelectableNodes: NodeType[];
  stageContext: RenderContext;
  selection: SelectionProvider;
  provider: Interactive;
}

export function createInteractive(renderContext: RenderContext): () => void {
  const canvas = renderContext.context.canvas;
  const { cursor } = renderContext.providers;
  const interactive = renderContext.interactive as Interactive;
  interactive.cursor = cursor;
  interactive.render = renderInteractive;
  interactive.stageContext = renderContext;
  interactive.requestFocus = () => {
    const parent = canvas.parentNode as HTMLElement | null;
    parent?.focus?.();
  };

  renderContext.providers.interactive = interactive;
  interactive.dispose = () => {};
  interactive.selectMode = 'default';
  interactive.createHitTestContext = createHitTestContext;
  interactive.screenX = 0;
  interactive.screenY = 0;
  // mobile touch flags
  interactive.isTouchEditingOrDragging = false;
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let touchStartPoint: ScreenPoint | null = null;
  const LONG_PRESS_MS = 450;
  const MOVE_TOLERANCE = 6 * renderContext.dpr;

  interactive.getMovingNodeInitialBox = (id) => {
    const node = renderContext.api.getNode(id);
    interactive.cacheMovingNode = interactive.cacheMovingNode || {};
    const exsit = interactive.cacheMovingNode[id];
    if (exsit) return exsit;
    const cache = {
      ...node,
    } as NodeType;
    interactive.cacheMovingNode[id] = cache;
    return cache;
  };

  // Clip mode state and helpers
  interactive.clipMode = null;
  interactive.enterClipMode = (node) => {
    if (!node || node.type !== TYPES.image) return;
    interactive.clipMode = {
      nodeId: node.id,
      state: {
        startPoint: null,
        cache: null,
        resizeEdge: null,
      },
    };
    interactive.cursor.set('');
    renderContext.api.markNeedRender();
  };
  interactive.exitClipMode = () => {
    if (interactive.clipMode) {
      interactive.clipMode = null;
      interactive.clipDrag = null;
      interactive.cursor.set('');
      renderContext.api.markNeedRender();
    }
  };

  // Clip mode state and helpers
  interactive.pathMode = null;
  interactive.enterPathMode = (node) => {
    if (!node || node.type !== TYPES.path) return;
    interactive.pathMode = {
      nodeId: node.id,
      state: {
        startPoint: null,
        cache: null,
        resizeEdge: null,
      },
    };
    interactive.cursor.set('');
    renderContext.api.markNeedRender();
  };

  interactive.exitPathMode = () => {
    if (interactive.pathMode) {
      interactive.pathMode = null;
    }
  };

  interactive.exitEditMode = () => {
    interactive.exitClipMode();
    interactive.exitPathMode();
    // also exit style brush mode
    interactive.brush = null;
  };

  // ESC to exit clip mode
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      interactive.exitEditMode();
    }
  };
  window.addEventListener('keydown', onKeyDown);

  interactive.eventToPoint = eventToPoint;
  function eventToPoint(e: InteractiveEvent): Point {
    const screenPoint = eventToScreenPoint(renderContext, e);
    const { scale: scaleProvider } = renderContext.providers;
    return scaleProvider.offsetTransform(screenPoint);
  }

  canvas.addEventListener('mousedown', (e: MouseEvent) => {
    // 不要阻止默认，交由 action pipeline 统一处理
    // 仅当未在编辑文本时才把焦点给容器，避免隐藏输入框失焦导致选区丢失
    try {
      const screenPoint = eventToScreenPoint(renderContext, e);
      const point = renderContext.providers.scale.offsetTransform(screenPoint);
      const node = getTextNodeAtPoint(renderContext, point);
      const isTextEditing = renderContext.providers.text.isEditing(node);
      if (!node) {
        // 点击非文字区域时，确保容器获得焦点
        interactive.requestFocus();
      } else if (renderContext.providers.text.isEditing(node)) {
        // 点击正在编辑的文字时，保持/恢复隐藏输入焦点
        renderContext.providers.text?.focus();
      } else {
        // 点击文字节点但尚未进入编辑，不要抢占焦点
        // 让后续的 up/双击逻辑决定是否进入编辑并聚焦隐藏输入
      }
      if (isTextEditing && e.button === 2) {
        renderContext.providers.text.cancel();
        return;
      }
      if (e.button === 2) {
        return;
      }
    } catch (err) {
      console.error(err);
      interactive.requestFocus();
    }

    eventHandler(e, 'down');
  });

  // Touch → mouse-like events mapping for mobile editing
  canvas.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      if (e.touches.length > 1) {
        // Let pinch/pan be handled by scale provider
        interactive.isTouchEditingOrDragging = false;
        clearLongPress();
        renderContext.providers.scale.isDisabled = false;
        return;
      }
      const t = e.touches[0];
      const fake = { clientX: t.clientX, clientY: t.clientY, target: canvas };
      touchStartPoint = eventToScreenPoint(renderContext, fake);
      interactive.isTouchEditingOrDragging = false;
      renderContext.providers.scale.isDisabled = true;
      startLongPress(e, fake);
      eventHandler(fake, 'down');
      // prevent native scroll
      e.preventDefault();
    },
    { passive: false }
  );

  canvas.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      if (e.touches.length > 1) {
        // Multi-touch is managed by scale provider; cancel long-press
        clearLongPress();
        interactive.isTouchEditingOrDragging = false;
        return;
      }
      const t = e.touches[0];
      const fake = { clientX: t.clientX, clientY: t.clientY, target: canvas };
      const current = eventToScreenPoint(renderContext, fake);
      if (
        touchStartPoint &&
        (Math.abs(current.x - touchStartPoint.x) > MOVE_TOLERANCE ||
          Math.abs(current.y - touchStartPoint.y) > MOVE_TOLERANCE)
      ) {
        // Moving cancels long press
        clearLongPress();
      }
      eventHandler(fake, 'move');
      // Mark dragging state when moving handlers/nodes
      interactive.isTouchEditingOrDragging = !!(
        interactive.movingHandler ||
        (interactive.movingNodes && interactive.movingNodes.length > 0)
      );
      e.preventDefault();
    },
    { passive: false }
  );

  canvas.addEventListener(
    'touchend',
    (e: TouchEvent) => {
      // Use changedTouches for the last point
      const t = e.changedTouches && e.changedTouches[0];
      if (t) {
        const fake = { clientX: t.clientX, clientY: t.clientY, target: canvas };
        eventHandler(fake, 'up', true);
      } else {
        eventHandler({ clientX: 0, clientY: 0, target: canvas } as InteractiveEvent, 'up', true);
      }
      clearLongPress();
      interactive.isTouchEditingOrDragging = false;
      // Re-enable stage panning/zoom after short delay to avoid flicker
      setTimeout(() => {
        renderContext.providers.scale.isDisabled = false;
      }, 0);
      e.preventDefault();
    },
    { passive: false }
  );

  let lastDragOver = null;
  canvas.addEventListener('dragover', (e: DragEvent) => {
    const apiNow =
      renderContext && renderContext.api && typeof renderContext.api.now === 'function'
        ? () => renderContext.api.now()
        : () => (performance && performance.now ? performance.now() : Date.now());
    if (lastDragOver && lastDragOver > apiNow() - 16) {
      return;
    }
    lastDragOver = apiNow();
    const point = eventToPoint(e);

    if (interactive.createNode) {
      if (!interactive.pointStart) {
        interactive.pointStart = point;
        const nodeData = interactive.createNode;
        let node = renderContext.api.getNode(nodeData.id);
        if (node) {
          renderContext.api.updateNode(node.id, {
            x: point.x - nodeData.w / 2,
            y: point.y - nodeData.h / 2,
          });
        } else {
          node = renderContext.api.addNode({
            ...interactive.createNode,
            x: point.x - nodeData.w / 2,
            y: point.y - nodeData.h / 2,
          });
        }
        renderContext.api.selectNode([node.id]);
        interactive.createNode = node;
        if (interactive.createNodeCallback) {
          interactive.createNodeCallback();
        }
        interactive.createNodeCallback = null;
      }
      const pointStart = interactive.pointStart;
      const node = interactive.createNode;
      const cache = interactive.getMovingNodeInitialBox(node.id);
      renderContext.api.updateNode(node.id, {
        x: cache.x + point.x - pointStart.x,
        y: cache.y + point.y - pointStart.y,
      });
    }
  });

  window.addEventListener('dragend', onDragEnd);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onMouseMove);

  function onDragEnd(e: DragEvent): void {
    if (interactive.createNode) {
      const node = interactive.createNode;
      const point = eventToPoint(e);
      const hitTestContext = createHitTestContext(
        point,
        eventToScreenPoint(renderContext, e),
        EventTypes.up,
        renderContext
      );
      const movingNodes = [
        {
          id: node.id,
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          node: node,
        },
      ];

      moveInOrOut(hitTestContext, movingNodes);
      if (node.type === TYPES.text) {
        renderContext.providers.text.create(node);
      } else {
        interactive.requestFocus();
      }
    } else {
      interactive.requestFocus();
    }
    cleanup();
  }

  function onMouseUp(e: MouseEvent): void {
    const target = e.target as HTMLElement | SVGElement | null;
    const className = target?.className;
    // Handle both string className and SVGAnimatedString (for SVG elements)
    const classNameString =
      typeof className === 'string' ? className : (className as any)?.baseVal || '';
    const fromStage = target && classNameString && classNameString.includes('seede-stage');
    eventHandler(e, 'up', fromStage);
  }

  function onMouseMove(e: MouseEvent): void {
    // 不要手动处理文字拖拽，交给 textSelectionAction
    eventHandler(e, 'move');
  }

  function cleanup(): void {
    const { interactive } = renderContext;
    interactive.movingNodes = [];
    interactive.previousSelectedNodes = [];
    interactive.previousPointDownStart = null;
    interactive.pointStart = null;
    interactive.maxMoveDistance = null;
    interactive.rotationState = null;
    interactive.isRealMove = null;
    interactive.areaStart = null;
    interactive.movingHandler = null;
    interactive.alignmentResult = null;
    interactive.createNode = null;
    interactive.altCreated = false;
    interactive.skipSelectFromUp = false;
    interactive.insertedNode = null;
    interactive.cacheMovingNode = {};
    interactive.moveOutCache = null;
    interactive.alignCache = null;
    // Ensure SnapEngine state is cleared between gestures
    try {
      getOrCreateSnapEngine(renderContext).end();
    } catch {}
    interactive.screenX = 0;
    interactive.screenY = 0;
    interactive.cursor?.reset();
    interactive.isTouchEditingOrDragging = false;
    clearLongPress();
    // 结束文字拖拽选区
    renderContext.providers.text?.endSelection?.();
    interactive.spacingState = null;
  }

  function renderInteractive(renderContext: RenderContext): void {
    const context = renderContext.context;
    const { selection } = renderContext.providers;
    const selectedNodes = renderContext.api.getSelectedNodes();
    const isMultiSelect = selectedNodes.length > 1;

    context.save();
    if (!isMultiSelect) {
      selectedNodes.map((node) => {
        const parentNode = node.parentId ? renderContext.api.getNode(node.parentId) : null;
        if (parentNode?.type === TYPES.group) {
          renderInteractiveGroupBorder(renderContext, parentNode);
        }
        renderInteractiveBorder(context, node, true);
      });
    }
    // Render hover borders, but skip nodes that are already selected (to avoid duplicate rendering)
    const selectedIds = new Set(selectedNodes.map((n) => n.id));
    selection.hovers
      .filter((node) => !selectedIds.has(node.id))
      .map((node) => {
        renderInteractiveBorder(context, node);
      });

    drawSelectionArea(context, renderContext, selectedNodes);
    drawAlignmentHelper(context, renderContext);

    if (!isMultiSelect) {
      selectedNodes.map((node) => {
        if (renderContext.api.shouldSkip(node)) return;
        renderInteractiveHandler(context, node, renderContext);
      });
    }

    // spacing and smart-layout handles are drawn inside drawSelectionArea()
    drawScrollbars(context, renderContext);
    drawMeasureGuides(context, renderContext);

    // Clip-mode overlay with custom handlers
    if (interactive.clipMode) {
      const node = renderContext.api.getNode(interactive.clipMode.nodeId);
      if (node) {
        renderClipOverlay(renderContext, node);
      }
    }

    context.restore();
  }

  function renderInteractiveBorder(
    context: CanvasRenderingContext2D,
    node: NodeType,
    labelSize?: boolean,
    options?: any
  ): void {
    // 特殊处理Line节点的边框
    if (node.type === TYPES.line) {
      return renderInteractiveLineBorder(renderContext, context, node, labelSize, options);
    }

    return renderInteractiveNodeBorder(renderContext, context, node, labelSize, options);
  }

  function eventHandler(e: InteractiveEvent, type: string, fromStage = false): void {
    renderContext.providers.selection.clearHover();
    interactive.hoverHandlerId = '';
    if ((renderContext as any).mode === 'presentation') return;

    const screenPoint = eventToScreenPoint(renderContext, e);
    const point = renderContext.providers.scale.offsetTransform(screenPoint);
    (point as any).fromStage = fromStage;

    const hitTestContext = createHitTestContext(
      point as ScreenPoint,
      screenPoint,
      type,
      renderContext
    );

    // 处理右键点击
    if (type === EventTypes.down && e.button === 2) {
      if (handleLineRightClick(hitTestContext) || handlePathRightClick(hitTestContext)) {
        e.preventDefault?.();
        return;
      }
    }

    const eventActions: ((hitContext: HitTestContext) => boolean | void)[] = [
      captureMoveDistanceAction,
      scrollbarMoveAction,
      clipEventActions,
      pathEditAction,
      sectionMoveAction,
      spacingAdjustAction,
      spacingEqualizeAction,
      smartLayoutAction,
      captureStateAction,
      moveModeAction,
      textSelectionAction,
      editTextAction,
      hoverAction,
      hoverHandlerAction,
      radiusAction,
      styleBrushAction,
      insertAction,
      doubleClickAction,
      regionRotateAction,
      regionMoveAction,
      regionResizeAction,
      resizeAction,
      moveAction,
      selectByRegionAction,
      selectAction,
      unselectAction,
    ];
    for (const action of eventActions) {
      if (action(hitTestContext)) {
        break;
      }
    }

    if (type === EventTypes.up) {
      cleanup();
    }

    renderContext.api.markNeedRender();
  }

  return () => {
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('dragend', onDragEnd);
    window.removeEventListener('keydown', onKeyDown);
    cursor.detach();
  };

  function startLongPress(_nativeEvent: TouchEvent, fakePointEvent: InteractiveEvent): void {
    clearLongPress();
    longPressTimer = setTimeout(() => {
      // Emulate a context action for Line/Path midpoint add on long press
      const screenPoint = eventToScreenPoint(renderContext, fakePointEvent);
      const point = renderContext.providers.scale.offsetTransform(screenPoint);
      const ctx = createHitTestContext(point, screenPoint, EventTypes.down, renderContext);
      if (handleLineRightClick(ctx) || handlePathRightClick(ctx)) {
        renderContext.api.markNeedRender();
      }
    }, LONG_PRESS_MS);
  }

  function clearLongPress(): void {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }
}

function getTextNodeAtPoint(renderContext: RenderContext, point: Point): NodeType | null {
  const nodes = renderContext.api.getNodes().slice().reverse();
  for (const n of nodes) {
    if (n.type !== TYPES.text) continue;
    if (!renderContext.api.shouldSkip(n)) {
      if (renderContext.api.getNodeRegion(n)?.contains(point.x, point.y)) return n;
    }
  }
  return null;
}

function hitTestNodeHandler(
  renderContext: RenderContext,
  point: Point,
  node: NodeType | null,
  region?: Region
): Handler | null {
  const handlers = getInteractiveHandlers(renderContext, node, region);
  for (const handler of handlers) {
    if (handler.contains(point.x, point.y)) {
      handler.node = node ?? undefined;
      return handler;
    }
  }
  return null;
}

export function getInteractiveHandlers(
  stageContext: RenderContext,
  node: NodeType | null,
  region?: Region
): Handler[] {
  region = region ?? (node ? stageContext.api.getNodeRegion(node) : undefined) ?? undefined;
  const { scale: scaleProvider, text, interactive } = stageContext.providers;
  if (!region || (text && text.isEditing(node))) return [];
  const scale = 1 / scaleProvider.scale;

  // 特殊处理Line节点
  if (node?.type === TYPES.line) {
    return getInteractiveLineHandlers(
      stageContext,
      node,
      region,
      scale
    ) as unknown[] as LinePointHandler[];
  }

  // Path 节点（仅直线锚点模式）
  if (node?.type === TYPES.path && interactive.pathMode) {
    return [...getInteractivePathHandlers(stageContext, node, region, scale)];
  }

  // Frame 也支持旋转手柄（与 Figma 一致）
  if (node?.type === TYPES.frame) {
    return [...getInteractiveEdgeHandlers(region, node, scale)];
  }

  if (node?.type === TYPES.image && interactive.clipMode) {
    const clipHandlers = getInteractiveImageClipHandlers(stageContext, node);
    return clipHandlers;
  }

  const styleConfig = stageContext.configs.interactive;
  // 当传入的是选区区域（node为空或为selection）时，增加"间距手柄"命中
  const isSelectionRegion = !node || !node.id;
  const baseHandlers: Handler[] = [
    ...getInteractiveEdgeHandlers(region, node, scale),
    ...getInteractivePointHandlers(region, scale, styleConfig.handlerSize),
    ...getRotateHandlers(region, node ?? { type: 'selection' }, scale),
  ];
  // 添加 section-move 句柄（支持单选与多选selection区域）
  const sectionHandlers = getSectionMoveHandlers(stageContext, node, region, scale);
  if (sectionHandlers && sectionHandlers.length) {
    baseHandlers.push(...sectionHandlers);
  }
  // Add radius handlers for rectangle shapes
  if (isRadiusableNode(node)) {
    return [...baseHandlers, ...getInteractiveRadiusHandlers(stageContext, node, region, scale)];
  }
  if (isSelectionRegion && stageContext.api.getSelectedNodes().length > 1) {
    return [
      ...baseHandlers,
      ...getSpacingHandlers(stageContext, region, scale),
      ...getSmartLayoutHandlers(stageContext),
    ];
  }
  return baseHandlers;
}

const defaultPointTypes: ('topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight')[] = [
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
];

function getInteractivePointHandlers(region: Region, scale = 1, size = 12): PointHandler[] {
  let pointTypes = defaultPointTypes;
  if (region.rect.w / scale < 64 || region.rect.h / scale < 64) {
    pointTypes = ['bottomRight'];
  }

  const rotatedControlPoints: {
    x: number;
    y: number;
    id: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    type: 'point';
  }[] = pointTypes.map((key) => {
    const point = region[key];
    return {
      ...point,
      id: key,
      type: 'point',
    };
  });

  const controlPointSize = size * scale;
  return rotatedControlPoints.map((point) => {
    const rect = new Rect(
      point.x - controlPointSize / 2,
      point.y - controlPointSize / 2,
      controlPointSize,
      controlPointSize
    ) as unknown as PointHandler;
    rect.id = point.id;
    rect.type = point.type;
    return rect;
  });
}

const defaultMidPoints: string[] = ['top', 'left', 'bottom', 'right'];

// 仅一个旋转手柄，位于上边中点外侧，常显
function getRotateHandlers(
  region: SelectionRegion,
  node: NodeType | { type: 'selection' } | null,
  scale = 1
): RotateHandler[] {
  if (!node) return [];
  if (node.type === TYPES.frame) return [];

  const hitRadius = 28 * scale;
  const visualRadius = 20 * scale;
  const offset = 56 * scale;

  // 判断元素的宽高比，决定旋转手柄位置
  const box = region.rect;
  const width = box.w;
  const height = box.h;
  const isSelection = region.type === 'selection' || node.type === 'selection';

  let anchorPoint;

  if (isSelection || Math.abs(width - height) < 2) {
    // 宽度大于等于高度，位于下方
    anchorPoint = {
      x: (region.bottomLeft.x + region.bottomRight.x) / 2,
      y: (region.bottomLeft.y + region.bottomRight.y) / 2,
    };
  } else {
    // 高度大于宽度，位于右侧
    anchorPoint = {
      x: (region.topRight.x + region.bottomRight.x) / 2,
      y: (region.topRight.y + region.bottomRight.y) / 2,
    };
  }

  // 使用中心到锚点的方向，确保手柄始终在外侧
  const cx = region.center.x;
  const cy = region.center.y;
  const vx = anchorPoint.x - cx;
  const vy = anchorPoint.y - cy;
  const vlen = Math.hypot(vx, vy) || 1;
  const nx = vx / vlen;
  const ny = vy / vlen;

  const hx = anchorPoint.x + nx * offset;
  const hy = anchorPoint.y + ny * offset;

  const rect = new Rect(
    hx - hitRadius,
    hy - hitRadius,
    hitRadius * 2,
    hitRadius * 2
  ) as unknown as RotateHandler;
  rect.id = 'rotate';
  rect.type = 'rotate';
  rect.anchor = anchorPoint;
  rect.visualRadius = visualRadius;
  return [rect];
}

function getInteractiveEdgeHandlers(
  region: Region,
  node: NodeType | null,
  scale = 1
): EdgeHandler[] {
  // Frame: use outside-centered, semi-transparent black rounded-rect handlers
  if (node && node.type === TYPES.frame) {
    let pointTypes = defaultMidPoints;
    if (region.rect.w / scale < 70 || region.rect.h / scale < 70) {
      pointTypes = ['right', 'bottom'];
    }

    function getAngle(p1, p2) {
      return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
    }

    const topMid = {
      x: (region.topLeft.x + region.topRight.x) / 2,
      y: (region.topLeft.y + region.topRight.y) / 2,
      id: 'top',
      type: 'edge',
      angle: getAngle(region.topLeft, region.topRight),
    };
    const rightMid = {
      x: (region.topRight.x + region.bottomRight.x) / 2,
      y: (region.topRight.y + region.bottomRight.y) / 2,
      vertical: true,
      id: 'right',
      type: 'edge',
      angle: getAngle(region.topRight, region.bottomRight) + 90,
    };
    const bottomMid = {
      x: (region.bottomLeft.x + region.bottomRight.x) / 2,
      y: (region.bottomLeft.y + region.bottomRight.y) / 2,
      id: 'bottom',
      type: 'edge',
      angle: getAngle(region.bottomLeft, region.bottomRight),
    };
    const leftMid = {
      x: (region.topLeft.x + region.bottomLeft.x) / 2,
      y: (region.topLeft.y + region.bottomLeft.y) / 2,
      vertical: true,
      id: 'left',
      type: 'edge',
      angle: getAngle(region.topLeft, region.bottomLeft) + 90,
    };

    let midControlPoints: {
      x: number;
      y: number;
      id: string;
      type: string;
      angle: number;
      vertical?: boolean;
    }[] = [topMid, rightMid, bottomMid, leftMid].filter((item) => pointTypes.includes(item.id));

    // FIXME
    // @ts-expect-error node should be text, but not sure
    if (node && node.height === LayoutSizeType.hug) {
      midControlPoints = midControlPoints.filter(
        (item) => item.id !== 'top' && item.id !== 'bottom'
      );
    }

    const rectWidth = 64 * scale;
    const rectHeight = 10 * scale;
    const cx = region.center.x;
    const cy = region.center.y;

    // Move handle center slightly outward from the edge midpoint
    const outwardGap = 12 * scale;

    return midControlPoints.map((point) => {
      const vx = point.x - cx;
      const vy = point.y - cy;
      const len = Math.hypot(vx, vy) || 1;
      const nx = vx / len;
      const ny = vy / len;

      // Use half thickness along normal plus a small gap to ensure fully outside
      const normalThickness = rectHeight; // consistent outward offset
      const dist = normalThickness / 2 + outwardGap;

      const hx = point.x + nx * dist;
      const hy = point.y + ny * dist;

      let rect;
      if (point.vertical) {
        rect = new Rect(hx - rectHeight / 2, hy - rectWidth / 2, rectHeight, rectWidth);
        rect = Region.fromRect(rect).rotate(point.angle);
      } else {
        rect = new Rect(hx - rectWidth / 2, hy - rectHeight / 2, rectWidth, rectHeight);
        rect = Region.fromRect(rect).rotate(point.angle);
      }

      rect.id = point.id;
      rect.type = point.type;
      rect.style = 'frameEdge';

      return rect;
    });
  }

  let pointTypes = defaultMidPoints;
  if (region.rect.w / scale < 70 || region.rect.h / scale < 70) {
    pointTypes = ['right', 'bottom'];
  }

  // 计算每个边的角度
  function getAngle(p1, p2) {
    return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
  }

  // 四个边的中点和角度
  const topMid = {
    x: (region.topLeft.x + region.topRight.x) / 2,
    y: (region.topLeft.y + region.topRight.y) / 2,
    id: 'top',
    type: 'edge',
    angle: getAngle(region.topLeft, region.topRight),
  };
  const rightMid = {
    x: (region.topRight.x + region.bottomRight.x) / 2,
    y: (region.topRight.y + region.bottomRight.y) / 2,
    vertical: true,
    id: 'right',
    type: 'edge',
    // 右侧控制点应垂直于边，即加90度
    angle: getAngle(region.topRight, region.bottomRight) + 90,
  };
  const bottomMid = {
    x: (region.bottomLeft.x + region.bottomRight.x) / 2,
    y: (region.bottomLeft.y + region.bottomRight.y) / 2,
    id: 'bottom',
    type: 'edge',
    angle: getAngle(region.bottomLeft, region.bottomRight),
  };
  const leftMid = {
    x: (region.topLeft.x + region.bottomLeft.x) / 2,
    y: (region.topLeft.y + region.bottomLeft.y) / 2,
    vertical: true,
    id: 'left',
    type: 'edge',
    // 左侧控制点应垂直于边，即加90度
    angle: getAngle(region.topLeft, region.bottomLeft) + 90,
  };

  let midControlPoints: {
    x: number;
    y: number;
    id: string;
    type: string;
    angle: number;
    vertical?: boolean;
  }[] = [topMid, rightMid, bottomMid, leftMid].filter((item) => pointTypes.includes(item.id));

  // FIXME
  // @ts-expect-error node should be text, but not sure
  if (node && node.height === LayoutSizeType.hug) {
    midControlPoints = midControlPoints.filter((item) => item.id !== 'top' && item.id !== 'bottom');
  }

  const rectWidth = 32 * scale;
  const rectHeight = 12 * scale;

  return midControlPoints.map((point) => {
    let rect;
    // 垂直方向的控制点需要加90度旋转
    if (point.vertical) {
      rect = new Rect(point.x - rectHeight / 2, point.y - rectWidth / 2, rectHeight, rectWidth);
      rect = Region.fromRect(rect).rotate(point.angle);
    } else {
      rect = new Rect(point.x - rectWidth / 2, point.y - rectHeight / 2, rectWidth, rectHeight);
      rect = Region.fromRect(rect).rotate(point.angle);
    }

    rect.id = point.id;
    rect.type = point.type;

    return rect;
  });
}

function drawSelectionArea(
  context: CanvasRenderingContext2D,
  stageContext: RenderContext,
  selectedNodes: NodeType[]
): void {
  const { interactive, scale } = stageContext.providers;
  const interactiveStyle = stageContext.configs?.interactive;
  if (interactive.areaStart && interactive.areaEnd) {
    const startX = interactive.areaStart.x;
    const startY = interactive.areaStart.y;
    const endX = interactive.areaEnd.x;
    const endY = interactive.areaEnd.y;
    const width = endX - startX;
    const height = endY - startY;
    const rect = new Rect(startX, startY, width, height);
    const box = scale.boxTransform(rect);
    drawRectRegion(context, box, { lineWidth: 2 });
    selectedNodes.map((node) => {
      renderInteractiveNodeBorder(stageContext, context, node, true, { lineWidth: 2 });
    });
  } else if (selectedNodes.length > 1) {
    const rect = stageContext.api.getSelectedNodesRegion();
    const box = scale.boxTransform(rect);
    drawRectRegion(context, box, {
      fill: 'transparent',
      lineWidth: interactiveStyle?.selectionLineWidth,
      stroke: interactiveStyle?.selectionColor,
    });
    selectedNodes.map((node) => {
      renderInteractiveNodeBorder(stageContext, context, node, true, { lineWidth: 2 });
    });
    const region = Region.fromRect(box) as SelectionRegion;
    region.type = 'selection';
    renderInteractiveHandler(context, box, stageContext, { region });
    drawSpacingHandles(context, stageContext);
    drawSmartLayoutButtons(context, stageContext);
  }
}

function renderInteractiveHandler(
  context: CanvasRenderingContext2D,
  node: NodeType | { x: number; y: number; w: number; h: number },
  stageContext: RenderContext,
  { region }: { region?: Region } = {}
): void {
  const { scale, interactive, text } = stageContext.providers;
  if (text && text.isEditing(node)) return;
  // FIXME
  toType<NodeType>(node);

  region =
    region ||
    stageContext.api
      .getNodeRegion(node)
      ?.transform(scale.scale, scale.translateX, scale.translateY);

  if (!region) return;

  // 特殊处理Line节点
  if (node.type === TYPES.line) {
    const lineHandlers = getInteractiveLineHandlers(stageContext, node, region);
    lineHandlers.forEach((area) => {
      const isActive = area.id === interactive.hoverHandlerId;
      if (area.type === 'linePoint') {
        renderLinePointHandler(context, area, isActive);
      } else if (area.type === 'lineMidPoint') {
        renderLineMidPointHandler(context, area, isActive);
      }
    });
    return;
  }

  // Path 节点的点编辑模式（支持曲线控制点）
  if (node.type === TYPES.path && interactive.pathMode) {
    const pathHandlers = getInteractivePathHandlers(stageContext, node, region);
    pathHandlers.forEach((area) => {
      const isActive = area.id === interactive.hoverHandlerId;
      if (area.type === 'pathControl') {
        renderPathControlHandler(context, area, isActive);
      } else if (area.type === 'pathPoint') {
        renderPathAnchorHandler(context, area, isActive);
      } else if (area.type === 'pathMidPoint') {
        renderLineMidPointHandler(context, area, isActive);
      }
    });
    return;
  }

  if (node.type === TYPES.image && interactive.clipMode) {
    return;
  }

  const isMoving = interactive.movingNodes && interactive.movingNodes.length;
  if (isMoving) {
    return;
  }

  if (node.type !== TYPES.frame) {
    const style = stageContext.configs.interactive;
    const controlPoints = getInteractivePointHandlers(region, undefined, style.handlerSize);
    controlPoints.forEach((area) => {
      if (interactive.movingHandler) {
        const isActive = area.id === interactive.movingHandler.id;
        if (isActive)
          renderPointHandler(context, area, isActive, stageContext.configs?.interactive);
      } else {
        const isActive = area.id === interactive.hoverHandlerId;
        renderPointHandler(context, area, isActive, stageContext.configs?.interactive);
      }
    });
  }

  const midControlPoints = getInteractiveEdgeHandlers(region, node);
  midControlPoints.forEach((area) => {
    if (interactive.movingHandler) {
      const isActive = area.id === interactive.movingHandler.id;
      if (isActive) renderEdgeHandler(context, area, isActive, stageContext.configs?.interactive);
    } else {
      const isActive = area.id === interactive.hoverHandlerId;
      renderEdgeHandler(context, area, isActive, stageContext.configs?.interactive);
    }
  });

  // section-move 底部中点句柄
  // 传递 scale=1/scaleProvider.scale，保持视觉恒定
  const sectionHandlers = getSectionMoveHandlers(stageContext, node, region, 1);
  sectionHandlers.forEach((area) => {
    if (interactive.movingHandler) {
      const isActive = area.id === interactive.movingHandler.id;
      if (isActive) renderSectionMoveHandler(context, area, isActive);
    } else {
      const isActive = area.id === interactive.hoverHandlerId;
      renderSectionMoveHandler(context, area, isActive);
    }
  });

  // 旋转手柄
  const rotateHandlers = getRotateHandlers(region, node);
  rotateHandlers.forEach((area) => {
    if (interactive.movingHandler) {
      const isActive = area.id === interactive.movingHandler.id;
      if (isActive) renderRotateHandler(context, area, isActive, stageContext.configs.interactive);
    } else {
      const isActive = area.id === interactive.hoverHandlerId;
      renderRotateHandler(context, area, isActive, stageContext.configs.interactive);
    }
  });

  // 绘制角度提示标签（旋转中或悬停旋转手柄时）
  if (rotateHandlers && rotateHandlers.length) {
    const area = rotateHandlers[0];
    const isRotating =
      (interactive.movingHandler && interactive.movingHandler.type === 'rotate') ||
      !!interactive.rotationState;
    const isHoveringRotate = interactive.hoverHandlerId === 'rotate';
    if (isRotating || isHoveringRotate) {
      const { action } = stageContext.providers;
      let deg: number | null = null;

      if (
        interactive.movingHandler &&
        interactive.movingHandler.type === 'rotate' &&
        interactive.movingHandler.rotate
      ) {
        const r = interactive.movingHandler.rotate;
        const curr = Math.atan2(interactive.y - r.center.y, interactive.x - r.center.x);
        const delta = curr - r.startAngle;
        deg = ((r.baseRotation + delta) * 180) / Math.PI;
        if (action.isShiftKeyPressed) deg = Math.round(deg / 15) * 15;
      } else if (interactive.rotationState) {
        const r = interactive.rotationState;
        const curr = Math.atan2(interactive.y - r.center.y, interactive.x - r.center.x);
        const delta = curr - r.startAngle;
        deg = ((r.baseRotation + delta) * 180) / Math.PI;
        if (action.isShiftKeyPressed) deg = Math.round(deg / 15) * 15;
      } else {
        // 非拖拽，仅悬停时显示当前区域角度
        deg = region && typeof region.angle === 'number' ? region.angle : 0;
      }

      // 归一化到 [-180, 180]
      const normalized = ((((deg + 180) % 360) + 360) % 360) - 180;
      const text = `${Math.round(normalized)}°`;

      // 位置：在旋转手柄外沿再偏移一定像素
      const center = area.center;
      const anchor = area.anchor || center;
      const vx = center.x - anchor.x;
      const vy = center.y - anchor.y;
      const len = Math.hypot(vx, vy) || 1;
      const nx = vx / len;
      const ny = vy / len;
      const baseOffset =
        typeof area.visualRadius === 'number' ? area.visualRadius : area.w ? area.w / 2 : 20;
      const offset = baseOffset + 32;
      const labelX = center.x + nx * offset;
      const labelY = center.y + ny * offset;

      drawAngleLabel(context, labelX, labelY, text);
    }
  }

  // 半径手柄（仅矩形形状）
  if (isRadiusableNode(node)) {
    const radiusHandlers = getInteractiveRadiusHandlers(stageContext, node, region, 1, scale.scale);
    radiusHandlers.forEach((area) => {
      const isActive =
        area.id === interactive.hoverHandlerId ||
        (interactive.movingHandler && area.id === interactive.movingHandler.id);
      renderRadiusHandler(context, area, isActive);
    });
  }
}

function drawAngleLabel(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  text: string
): void {
  if (!text) return;
  context.save();

  // Text style
  const fontSize = 24;
  context.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;
  context.textBaseline = 'middle';
  context.textAlign = 'center';

  const metrics = context.measureText(text);
  const textWidth = Math.ceil(metrics.width);
  const paddingX = 8;
  const paddingY = 4;
  const boxWidth = textWidth + paddingX * 2;
  const boxHeight = fontSize + paddingY * 2;
  const r = Math.min(10, boxHeight / 2);

  // Background pill with subtle shadow
  context.shadowColor = 'rgba(0,0,0,0.25)';
  context.shadowBlur = 6;
  context.shadowOffsetY = 2;

  const left = x - boxWidth / 2;
  const top = y - boxHeight / 2;

  context.beginPath();
  // Rounded rect
  context.moveTo(left + r, top);
  context.lineTo(left + boxWidth - r, top);
  context.quadraticCurveTo(left + boxWidth, top, left + boxWidth, top + r);
  context.lineTo(left + boxWidth, top + boxHeight - r);
  context.quadraticCurveTo(left + boxWidth, top + boxHeight, left + boxWidth - r, top + boxHeight);
  context.lineTo(left + r, top + boxHeight);
  context.quadraticCurveTo(left, top + boxHeight, left, top + boxHeight - r);
  context.lineTo(left, top + r);
  context.quadraticCurveTo(left, top, left + r, top);

  context.fillStyle = 'rgba(17, 24, 39, 0.85)';
  context.fill();

  // Reset shadow for text
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  // Text
  context.fillStyle = '#ffffff';
  context.fillText(text, x, y);

  context.restore();
}

export function hitTestNode(stageContext: RenderContext, point: Point, node: NodeType): boolean {
  // FIXME
  // @ts-expect-error no reason to put isSeedeBadge on node
  if (node.isSeedeBadge === true || node.pointerEvents === 'none') return false;
  // 特殊处理Line节点
  if (node.type === TYPES.line) {
    return hitTestLineNode(stageContext, point, node);
  }

  const region = stageContext.api.getNodeRegion(node);
  return region?.contains(point.x, point.y) ?? false;
}

function hitTestNodeWithChildren(
  stageContext: RenderContext,
  point: Point,
  node: NodeType
): NodeType | null {
  // 当节点被锁定时，仅命中测试该节点本身，不允许选择其子节点
  if (node.locked === true) {
    return hitTestNode(stageContext, point, node) ? node : null;
  }
  if (
    node.type !== TYPES.group &&
    node.type !== TYPES.frame &&
    node.type !== TYPES.col &&
    node.type !== TYPES.row
  ) {
    return hitTestNode(stageContext, point, node) ? node : null;
  } else {
    const children = stageContext.api.getChildren(node.id).reverse();
    for (const child of children) {
      if (hitTestNode(stageContext, point, child)) return child;
    }
    return hitTestNode(stageContext, point, node) ? node : null;
  }
}

function testNodeAndDescendants(
  stageContext: RenderContext,
  point: Point,
  node: NodeType
): NodeType | null {
  // 若当前节点被锁定，仅测试节点本身，不再递归子节点
  if (node.locked === true) {
    return hitTestNode(stageContext, point, node) ? node : null;
  }
  // Recursive case: test all children first
  const children = stageContext.api.getChildren(node.id).reverse();
  for (const child of children) {
    const result = testNodeAndDescendants(stageContext, point, child);
    if (result) return result;
  }

  // If no children matched, test the node itself
  return hitTestNode(stageContext, point, node) ? node : null;
}

function createHitTestContext(
  point: Point,
  screenPoint: ScreenPoint,
  type: string,
  stageContext: RenderContext,
  { skipIds = [] }: { skipIds?: string[] } = {}
): HitTestContext {
  const interactive = stageContext.interactive;
  const deltaX = point.x - (interactive.x || 0);
  const deltaY = point.y - (interactive.y || 0);
  interactive.x = point.x;
  interactive.y = point.y;

  const screenDeltaX = screenPoint.x - (interactive.screenX || 0);
  const screenDeltaY = screenPoint.y - (interactive.screenY || 0);
  interactive.screenX = screenPoint.x;
  interactive.screenY = screenPoint.y;

  let targetNode: NodeType | null = null;
  let shallowNode: NodeType | null = null;
  let depthNode: NodeType | null = null;
  let targetHandler: Handler | null = null;
  let targetRegion: (Rect & { nodes?: NodeType[] }) | null = null;
  let targetSelectedNode: NodeType | null = null;

  const selectedNodes = stageContext.api.getSelectedNodes();
  const currentSelectableNodes = getCurrentSelectableNodes(stageContext, skipIds);
  const scrollbarHandlers = getScrollbarHandlers(stageContext);
  for (const handler of scrollbarHandlers) {
    if (handler.contains(screenPoint.x, screenPoint.y)) {
      targetHandler = handler;
      break;
    }
  }

  if (!targetHandler) {
    const { action } = stageContext.providers;
    const isMetaKeyPressed = action.isMetaKeyPressed;
    const nodes = currentSelectableNodes;
    const isMultiSelect = selectedNodes.length > 1;

    let isRegionMatch = false;
    if (isMultiSelect) {
      const rect = stageContext.api.getSelectedNodesRegion();
      const region = Region.fromRect(rect);
      const handler = hitTestNodeHandler(stageContext, point, null, region);
      if (handler) {
        targetHandler = handler;
        isRegionMatch = true;
      }
      if (!targetHandler) {
        isRegionMatch = rect.contains(point.x, point.y);
        if (isRegionMatch) {
          targetRegion = rect;
          targetRegion.nodes = selectedNodes;
        }
      }
    }

    if (!isRegionMatch) {
      const isDeepMode = isMetaKeyPressed || interactive.brush;
      if (!isDeepMode) {
        for (const node of selectedNodes) {
          if (skipIds.includes(node.id)) continue;
          const handler = hitTestNodeHandler(stageContext, point, node);
          if (handler) {
            targetHandler = handler;
            targetNode = node;
            break;
          } else {
            if (node.type === TYPES.frame) {
              const result = hitTestNodeWithChildren(stageContext, point, node);
              if (result) {
                targetNode = result;
                break;
              }
            } else {
              const result = hitTestNode(stageContext, point, node);
              if (result) {
                targetSelectedNode = node;
                break;
              }
            }
          }
        }
      } else {
        const topNodes = stageContext.api.getChildren(null);
        for (const node of topNodes) {
          const result = testNodeAndDescendants(stageContext, point, node);
          if (result) {
            targetNode = result;
            break;
          }
        }
      }

      if (!targetNode && !targetHandler) {
        for (const node of selectedNodes) {
          const handler = hitTestNodeHandler(stageContext, point, node);
          if (handler) {
            targetHandler = handler;
            targetNode = node;
            break;
          }
        }

        if (!targetNode) {
          for (const node of nodes) {
            const result = hitTestNode(stageContext, point, node);
            if (result) {
              targetNode = node;
              break;
            }
          }
        }
      }
    }

    if (!targetNode) {
      for (const node of nodes) {
        const result = hitTestNode(stageContext, point, node);
        if (result) {
          shallowNode = node;
          break;
        }
      }
    }

    for (const node of selectedNodes) {
      const result = hitTestNodeWithChildren(stageContext, point, node);
      if (result) {
        depthNode = result;
        break;
      }
    }
  }

  return {
    type,
    point,
    screenPoint,
    deltaX,
    deltaY,
    screenDeltaX,
    screenDeltaY,
    targetNode,
    shallowNode,
    depthNode,
    targetHandler,
    targetSelectedNode,
    targetRegion,
    selectedNodes,
    currentSelectableNodes,
    stageContext: stageContext,
    selection: stageContext.providers.selection,
    provider: stageContext.providers.interactive,
  };
}

// 新增:Line节点右键菜单处理
function handleLineRightClick(hitTestContext: HitTestContext): boolean {
  const node = hitTestContext.targetNode || hitTestContext.shallowNode;

  if (!node || node.type !== TYPES.line) return false;

  // 检查是否点击在线段中点上
  if (hitTestContext.targetHandler && hitTestContext.targetHandler.type === 'lineMidPoint') {
    addPointAtSegment(hitTestContext);
    return true;
  }

  return false;
}

// 新增:Path节点右键菜单处理（仅直线锚点路径）
function handlePathRightClick(hitTestContext: HitTestContext): boolean {
  const node = hitTestContext.targetNode || hitTestContext.shallowNode;
  if (!node || node.type !== TYPES.path) return false;

  if (hitTestContext.targetHandler && hitTestContext.targetHandler.type === 'pathMidPoint') {
    const segmentIndex = hitTestContext.targetHandler.segmentIndex;
    const contextNode = node;
    const { api } = hitTestContext.stageContext;
    api.updateNode(contextNode.id, {
      path: addPathPointAtSegment(contextNode.path, segmentIndex, false),
    });
    return true;
  }
  return false;
}
